/**
 * «Anticipa»: la commessa entra in pipeline logistica come anticipo, con la data
 * prevista di pronto — si pre-avvisa il cliente mentre si finisce di confezionare.
 *
 * Stava dentro `M1OrderReadinessPage`. È uscita il 23-09-2026 perché il secondo
 * chiamante è il **drawer del Cruscotto a tre colonne**, e importare un pezzo da
 * una pagina che a sua volta importa il Cruscotto sarebbe un ciclo.
 */
import { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { M1OrdineRaggruppato } from "@/types/api";

export function AnticipaDialog({
  ordine,
  onClose,
  onConfirm,
  isPending,
}: {
  ordine: M1OrdineRaggruppato | null;
  onClose: () => void;
  onConfirm: (dataPronta: string) => void;
  isPending: boolean;
}) {
  const defaultData = ordine?.data_spedibilita_max
    ? ordine.data_spedibilita_max.split("T")[0]
    : "";
  const [dataPronta, setDataPronta] = useState(defaultData);

  // sync default quando cambia ordine
  useEffect(() => {
    if (ordine?.data_spedibilita_max) {
      setDataPronta(ordine.data_spedibilita_max.split("T")[0]);
    }
  }, [ordine]);

  if (!ordine) return null;
  return (
    <Dialog open={!!ordine} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Anticipa commessa #{ordine.numero_ordine}</DialogTitle>
          <DialogDescription>
            La commessa entrerà in pipeline come anticipo. Pre-avvisi il cliente
            così può organizzare il corriere mentre noi finiamo di confezionare.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <p className="text-xs text-muted-foreground">Cliente</p>
            <p className="text-sm font-medium">{ordine.cliente_ragione_sociale}</p>
          </div>
          <div>
            <label
              htmlFor="data-pronta"
              className="text-xs text-muted-foreground block mb-1"
            >
              Data prevista di pronto (suggerita da BC)
            </label>
            <input
              id="data-pronta"
              type="date"
              value={dataPronta}
              onChange={(e) => setDataPronta(e.target.value)}
              className="w-full h-9 rounded-md border bg-card px-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <button
            type="button"
            onClick={onClose}
            disabled={isPending}
            className="px-3 py-1.5 rounded-md text-xs hover:bg-accent/40 disabled:opacity-50"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={() => onConfirm(dataPronta)}
            disabled={!dataPronta || isPending}
            className="px-3 py-1.5 rounded-md text-xs font-medium bg-foreground text-background hover:bg-foreground/90 disabled:opacity-50"
          >
            {isPending ? "Anticipo in corso…" : "Anticipa"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
